/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Input, Row, Select, Slider, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
// Misc imports
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';
import { WEBCHAT_WIDGET_MODES } from '../../../../constants/main';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../../constants/regex';
import { capitalize } from '../../../../utils/string/capitalize';

const FORM_CONFIGURATION_LAYOUT_PATH = ['settings', 'layout', 'microsoftWebChat', 'configuration'];

const SUGGESTED_HEADER_STYLES = [
  'center',
  'left'
];

const SUGGESTED_FONTS = [
  'Arial',
  'Verdana',
  'Helvetica',
  'Tahoma',
  'Trebuchet MS',
  'Times New Roman',
  'Georgia',
  'Garamond',
  'Courier New',
  'Brush Script MT',
  'Roboto'
];

const BotDeploymentsWebchatLayoutGeneralForm = ({
  form,
  formData = {},
  isCreateMode = false,
  userRoles = {}
}) => {

  const { t } = useTranslation();
  const { borderRadiusBase, formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);

  return (
    <>
      <Row gutter={formProps.rowGutter}>
        <Col xs={24}>
          <Typography.Text>
            {t('General Settings')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t(`Create unique experiences with custom layout settings according to your deployment's needs.`)}
          </Typography.Paragraph>
        </Col>

        <Col xs={24}>
          <Form.Item
            label={t('Mode')}
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'mode']}
          >
            {
              hasEditAccess ? (
                <Select
                  placeholder={t('Select mode')}
                  dropdownMatchSelectWidth={false}
                  defaultValue={WEBCHAT_WIDGET_MODES.BUBBLE}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  {
                    Object.values(WEBCHAT_WIDGET_MODES).map((position, index) => (
                      <Select.Option key={index} value={position}>
                        {`${position[0].toUpperCase() + position.slice(1).toLowerCase()}`}
                      </Select.Option>
                    ))
                  }
                </Select>
              ) : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'mode'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Widget Radius')}
            className="w-100"
            initialValue={20}
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'widgetRadius']}
          >
            {
              hasEditAccess ? (
                <Slider
                  min={0}
                  max={32}
                  className="custom-slider px-display"
                  marks={{ 0: '0px', 20: 'Default', 32: '32px' }}
                  handleStyle={{ borderRadius: borderRadiusBase }}
                  tooltip={{ open: false }}
                />
              ) : (
                <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'widgetRadius'])}px</Typography.Text>
              )
            }
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const mode = getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'mode']);
              const isEmbedded = mode === WEBCHAT_WIDGET_MODES.EMBEDDED;
              return (
                <Fragment>
                  <Form.Item
                    label={t('Widget Width')}
                    className="w-100"
                    initialValue={376}
                    name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'width']}
                    hidden={isEmbedded}
                  >
                    {
                      hasEditAccess ? (
                        <Slider
                          min={350}
                          max={500}
                          className="custom-slider px-display"
                          marks={{ 350: '350px', 376: 'Default', 500: '500px' }}
                          handleStyle={{ borderRadius: borderRadiusBase }}
                          tooltip={{ open: false }}
                        />
                      ) : (
                        <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'width'])}px</Typography.Text>
                      )
                    }
                  </Form.Item>

                  <Form.Item
                    label={t('Widget Height')}
                    className="w-100"
                    initialValue={650}
                    name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'height']}
                    hidden={isEmbedded}
                  >
                    {
                      hasEditAccess ? (
                        <Slider
                          min={450}
                          max={850}
                          step={10}
                          className="custom-slider px-display"
                          marks={{ 450: '450px', 650: 'Default', 850: '850px' }}
                          handleStyle={{ borderRadius: borderRadiusBase }}
                          tooltip={{ open: false }}
                        />
                      ) : (
                        <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'height'])}px</Typography.Text>
                      )
                    }
                  </Form.Item>
                </Fragment>
              );
            }}
          </Form.Item>

          <Form.Item
            label={t('Header Style')}
            className="w-100"
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'headerStyle']}
            initialValue="center"
          >
            {
              hasEditAccess ? (
                <Select
                  placeholder={t('Select header style')}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  {SUGGESTED_HEADER_STYLES.map(item => (
                    <Select.Option key={item} value={item}>{capitalize(item)}</Select.Option>
                  ))}
                </Select>
              ) : (
                <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'headerStyle'])}px</Typography.Text>
              )
            }
          </Form.Item>

          <Form.Item
            label={t('Agent Header')}
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'headerText']}
          >
            {
              hasEditAccess
                ? <Input allowClear placeholder={t('Enter agent header')}/>
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'headerText'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={
              <Typography.Text>
                {t('Font Family')}

                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('You can select alternative fonts in case the primary one is not supported by the system of the end-user.')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Typography.Text>
            }
            name="fontFamily"
          >
            {
              hasEditAccess ? (
                <Select
                  placeholder={t('Select font family')}
                  mode="tags"
                  allowClear
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  {SUGGESTED_FONTS.map(item => (
                    <Select.Option key={item} value={item}>{item}</Select.Option>
                  ))}
                </Select>
              ) : <Typography.Text>{form.getFieldValue('fontFamily')}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={
              <Typography.Text>
                {t('Link Behavior')}

                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('Select whether links and link buttons are opened in the current tab or in a new one.')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Typography.Text>
            }
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, 'linkTarget']}
          >
            {
              hasEditAccess ? (
                <Select
                  placeholder={t('Select link behavior')}
                  showSearch={false}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                >
                  <Select.Option value="_blank">{t('Open in new tab')}</Select.Option>
                  <Select.Option value="_self">{t('Open in current tab')}</Select.Option>
                </Select>
              ) : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, 'linkTarget'])}</Typography.Text>
            }
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

BotDeploymentsWebchatLayoutGeneralForm.propTypes = {
  formData: PropTypes.object,
  isCreateMode: PropTypes.bool,
  userRoles: PropTypes.object,
  form: PropTypes.object
};

export default BotDeploymentsWebchatLayoutGeneralForm;
